import React, { useCallback, useMemo, useRef, useState } from 'react';
import { CButton, CCol, CForm, CRow } from '@coreui/react';
import { Formik } from 'formik';
import CFormGroup from '../../../../../migration/CFormGroup';
import CLabel from '../../../../../migration/CLabel';
import { CInput, CInputCheckbox, CTextarea } from '../../../../../migration/CInput';
import { CInvalidFeedback } from '../../../../../migration/CInvalidFeedback';
import { useDispatch } from 'react-redux';
import { setFlexibleMultiModal } from '../../../../../../actions/common';
import { COMPONENT_NAME } from '../../../../../../constants';
import { ConfirmSaveChange } from '../../../../../general/popup';

const FormGroup = ({
    name,
    label,
    errors,
    values,
    type = 'text',
    ...props
}) => {
    return (
        <CFormGroup className="mb-3">
            <CLabel htmlFor={name}>{label}</CLabel>
            {type === 'text' ? (
                <CInput
                    id={name}
                    invalid={!!errors[name]}
                    value={values[name]}
                    {...props}
                />
            ) : (
                <CTextarea
                    id={name}
                    invalid={!!errors[name]}
                    value={values[name]}
                    className="py-2 px-3 lh-base"
                    rows={4}
                    {...props}
                />
            )}
            <CInvalidFeedback>{errors[name]}</CInvalidFeedback>
        </CFormGroup>
    );
}

const ReportHeader = ({ name, description }) => (
    <CRow className="mb-2">
        <CCol lg="3"><h6>{name}</h6></CCol>
        <CCol lg="6" className="d-flex align-items-end pb-2"><h6>{description}</h6></CCol>
        <CCol lg="3" className="d-flex justify-content-center"><h6>Customize this Value</h6></CCol>
    </CRow>
);

const DataSourceEditField = ({ id, descriptionOverride, setFieldValue: setParentFieldValue, index: parentIndex, originalReportData }) => {
    const dispatch = useDispatch();
    const parsedDescriptionOverride = typeof descriptionOverride === 'string' ? JSON.parse(descriptionOverride) : descriptionOverride;
    const initialValues = useMemo(() => {
        const base = {
            reportName: originalReportData?.name || '',
            isReportNameCustomize: false,
            reportDescription: originalReportData?.description || '',
            isReportDescriptionCustomize: false,
            // Map original segments, dimensions, metrics and add isCustomize flag
            reportModels: (originalReportData?.models || []).map(mod => ({
                ...mod, // Assuming original model has name and description
                modelName: mod.name, // Ensure field names match map below
                modelDescription: mod.description,
                isExcluded: false,
                isModelCustomize: false,
            })),
            reportSegment: (originalReportData?.segments || []).map(seg => ({
                ...seg, // Assuming original segment has name and description
                segmentName: seg.name, // Ensure field names match map below
                segmentDescription: seg.description,
                isExcluded: false,
                isSegmentCustomize: false,
            })),
            reportDimensions: (originalReportData?.dimensions || []).map(dim => ({
                ...dim, // Assuming original dimension has name and description
                dimensionName: dim.name,
                dimensionDescription: dim.description,
                isExcluded: false,
                isDimensionCustomize: false,
            })),
            reportMetrics: (originalReportData?.metrics || []).filter((item) => !['Profile', 'View in Salesforce'].includes(item.metricName)).map(met => ({
                ...met, // Assuming original metric has name and description
                metricName: met.name,
                metricDescription: met.description,
                isExcluded: false,
                isMetricCustomize: false,
            })),
        };

        // If override exists, merge it carefully
        if (parsedDescriptionOverride) {
            return {
                ...base,
                reportName: parsedDescriptionOverride.reportName ?? base.reportName,
                isReportNameCustomize: parsedDescriptionOverride.isReportNameCustomize ?? base.isReportNameCustomize,
                reportDescription: parsedDescriptionOverride.reportDescription ?? base.reportDescription,
                isReportDescriptionCustomize: parsedDescriptionOverride.isReportDescriptionCustomize ?? base.isReportDescriptionCustomize,
                // Merge arrays carefully, matching by name/id if possible, or just override based on index if structure is guaranteed
                // This example assumes override array structure matches base structure if present
                reportModels: (parsedDescriptionOverride.reportModels ?? base.reportModels).map((model, idx) => ({
                    ...(base.reportModels[idx] || {}), // Start with base defaults for safety
                    ...model, // Override with parsed values
                    isExcluded: model.isExcluded ?? (base.reportModels[idx]?.isExcluded ?? false),
                    isModelCustomize: model.isModelCustomize ?? (base.reportModels[idx]?.isModelCustomize ?? false)
                })),
                reportSegment: (parsedDescriptionOverride.reportSegment ?? base.reportSegment).map((seg, idx) => ({
                    ...(base.reportSegment[idx] || {}), // Start with base defaults for safety
                    ...seg, // Override with parsed values
                    isExcluded: seg.isExcluded ?? (base.reportSegment[idx]?.isExcluded ?? false),
                    isSegmentCustomize: seg.isSegmentCustomize ?? (base.reportSegment[idx]?.isSegmentCustomize ?? false)
                })),
                reportDimensions: (parsedDescriptionOverride.reportDimensions ?? base.reportDimensions).map((dim, idx) => ({
                    ...(base.reportDimensions[idx] || {}),
                    ...dim,
                    isExcluded: dim.isExcluded ?? (base.reportDimensions[idx]?.isExcluded ?? false),
                    isDimensionCustomize: dim.isDimensionCustomize ?? (base.reportDimensions[idx]?.isDimensionCustomize ?? false),
                })),
                reportMetrics: (parsedDescriptionOverride.reportMetrics ?? base.reportMetrics).filter((item) => !['Profile', 'View in Salesforce'].includes(item.metricName)).map((met, idx) => ({
                    ...(base.reportMetrics[idx] || {}),
                    ...met,
                    isExcluded: met.isExcluded ?? (base.reportMetrics[idx]?.isExcluded ?? false),
                    isMetricCustomize: met.isMetricCustomize ?? (base.reportMetrics[idx]?.isMetricCustomize ?? false),
                })),
            };
        }
        return base;
    }, [originalReportData, parsedDescriptionOverride]);

    const setFieldValueRef = useRef(null);
    const [resetData, setResetData] = useState(null);
    const [showResetConfirm, setShowResetConfirm] = useState(false);

    const closeModal = () => {
        dispatch(
            setFlexibleMultiModal({
                show: false,
                component: COMPONENT_NAME.AI_INSIGHTS_DATA_SOURCE_EDIT_FIELD,
            })
        );
    };

    const handleClose = () => closeModal();

    const onSubmit = useCallback(
        values => {
            setParentFieldValue(`dataSources.${parentIndex}.descriptionOverride`, JSON.stringify(values));
            closeModal();
        }, [setParentFieldValue, parentIndex]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleResetDefault = useCallback((fieldInfo, formikSetFieldValue) => {
        setShowResetConfirm(true); // Show the modal
        setResetData(fieldInfo); // Store info about the field being reset
        setFieldValueRef.current = formikSetFieldValue; // Store Formik's setFieldValue for use in onAccept
    }, []);

    const onAcceptReset = useCallback(() => {
        if (!resetData || !setFieldValueRef.current) return;

        const {
            valueFieldName, // Full path to value field e.g., 'reportSegment[0].segmentDescription'
            checkboxFieldName, // Full path to checkbox field e.g., 'reportSegment[0].isSegmentCustomize'
            originalDataSourceKey, // Key in the original item e.g., 'description'
            originalItem, // The original item object from originalReportData array
            type // Type of the field (text, textarea, etc.)
        } = resetData;
        const formikSetFieldValue = setFieldValueRef.current;

        // Get default value from the original item passed in fieldInfo
        const defaultValue = originalItem?.[originalDataSourceKey] || '';

        // Use the full paths to set values
        formikSetFieldValue(valueFieldName, defaultValue);
        if (type === 'checked') formikSetFieldValue(checkboxFieldName, false); // Uncheck the customize box

        setShowResetConfirm(false); // Hide modal
        setResetData(null); // Clear reset data
        setFieldValueRef.current = null; // Clear ref
    }, [resetData]); // Depend on resetData and originalReportData

    // Function to close the reset confirmation modal
    const onCloseReset = () => {
        setShowResetConfirm(false);
        setResetData(null);
        setFieldValueRef.current = null;
    };

    const handleCustomizeChange = useCallback((checked, fieldInfo, formikSetFieldValue) => {
        const { checkboxFieldName } = fieldInfo;

        if (!checked) {
            // If unchecking, trigger the reset confirmation
            handleResetDefault(fieldInfo, formikSetFieldValue);
            // We don't immediately call setFieldValue for the checkbox here,
            // it will be handled in onAcceptReset if confirmed.
        } else {
            // If checking, just update the checkbox state
            formikSetFieldValue(checkboxFieldName, true);
        }
    }, [handleResetDefault]);

    return (
        <div className="rule-detail account-connected-detail">
            <div className="cvr-create-new">
                <h1 className='mb-3'>Edit Field Descriptions: {initialValues?.reportName}</h1>
                <p>The descriptions of each data source and it's fields will be provided to the the AI model to improve insights. You can edit the default descriptions in order to customize them only for thie AI Insights Template.</p>
            </div>

            <Formik
                initialValues={initialValues}
                onSubmit={onSubmit}
                enableReinitialize
            >
                {({ values, errors, handleChange, handleSubmit, setFieldValue: formikSetFieldValue }) => {
                    return (
                         <CForm onSubmit={handleSubmit} noValidate>
                              <CRow>
                                   <CCol lg="6">
                                        <FormGroup
                                             name="reportName"
                                             label="Report Name"
                                             values={values}
                                             errors={errors}
                                             onChange={handleChange}
                                             disabled={!values.isReportNameCustomize}
                                        />
                                   </CCol>

                                   <CCol lg="6" className="d-flex align-items-end pb-3">
                                        <CFormGroup variant="custom-checkbox" inline>
                                             <CInputCheckbox
                                                  custom
                                                  id="isReportNameCustomize"
                                                  name="isReportNameCustomize"
                                                  checked={values.isReportNameCustomize}
                                                  className="m-auto"
                                                  onChange={(e) =>
                                                       handleCustomizeChange(
                                                            e.target.checked,
                                                            {
                                                                 displayName: 'Report Name',
                                                                 valueFieldName: 'reportName', // Full path for simple field
                                                                 checkboxFieldName: 'isReportNameCustomize', // Full path for simple field
                                                                 originalDataSourceKey: 'name', // Key in originalReportData
                                                                 originalItem: originalReportData, // Pass the whole original object
                                                                 type: 'checked', // Type of the field (text, textarea, etc.)
                                                            },
                                                            formikSetFieldValue
                                                       )
                                                  }
                                             />
                                             <CLabel htmlFor="isReportNameCustomize" variant="custom-checkbox">
                                                  Customize this value
                                             </CLabel>
                                        </CFormGroup>

                                        {values.isReportNameCustomize && (
                                             <CButton
                                                  type="button"
                                                  className="btn-sm ml-4 text-underline text-capitalize"
                                                  color="link"
                                                  onClick={() =>
                                                       handleCustomizeChange(
                                                            false,
                                                            {
                                                                 displayName: 'Report Name',
                                                                 valueFieldName: 'reportName', // Full path for simple field
                                                                 checkboxFieldName: 'isReportNameCustomize', // Full path for simple field
                                                                 originalDataSourceKey: 'name', // Key in originalReportData
                                                                 originalItem: originalReportData, // Pass the whole original object
                                                                 type: 'button', // Type of the field (text, textarea, etc.)
                                                            },
                                                            formikSetFieldValue
                                                       )
                                                  }
                                             >
                                                  Reset to default
                                             </CButton>
                                        )}
                                   </CCol>
                              </CRow>

                              <CRow>
                                   <CCol lg="9">
                                        <FormGroup
                                             name="reportDescription"
                                             label="Description"
                                             type="textarea"
                                             values={values}
                                             errors={errors}
                                             onChange={handleChange}
                                             disabled={!values.isReportDescriptionCustomize}
                                        />
                                   </CCol>

                                   <CCol lg="3" className="d-flex align-items-end pb-3">
                                        <CFormGroup variant="custom-checkbox" inline>
                                             <CInputCheckbox
                                                  custom
                                                  id="isReportDescriptionCustomize"
                                                  name="isReportDescriptionCustomize"
                                                  checked={values.isReportDescriptionCustomize}
                                                  className="m-auto"
                                                  onChange={(e) =>
                                                       handleCustomizeChange(
                                                            e.target.checked,
                                                            {
                                                                 displayName: 'Report Description',
                                                                 valueFieldName: 'reportDescription',
                                                                 checkboxFieldName: 'isReportDescriptionCustomize',
                                                                 originalDataSourceKey: 'description',
                                                                 originalItem: originalReportData,
                                                                 type: 'checked',
                                                            },
                                                            formikSetFieldValue
                                                       )
                                                  }
                                             />
                                             <CLabel htmlFor="isReportDescriptionCustomize" variant="custom-checkbox">
                                                  Customize this value
                                             </CLabel>
                                        </CFormGroup>

                                        {values.isReportDescriptionCustomize && (
                                             <CButton
                                                  type="button"
                                                  className="btn-sm ml-4 text-underline text-capitalize"
                                                  color="link"
                                                  onClick={() =>
                                                       handleCustomizeChange(
                                                            false,
                                                            {
                                                                 displayName: 'Report Description',
                                                                 valueFieldName: 'reportDescription',
                                                                 checkboxFieldName: 'isReportDescriptionCustomize',
                                                                 originalDataSourceKey: 'description',
                                                                 originalItem: originalReportData,
                                                                 type: 'button',
                                                            },
                                                            formikSetFieldValue
                                                       )
                                                  }
                                             >
                                                  Reset to default
                                             </CButton>
                                        )}
                                   </CCol>
                              </CRow>

                              {/* Segments */}
                              <div className="mt-3">
                                   <h3 className="f-15">Segments</h3>
                                   <p className="f-15">
                                        If your report contains segments, they are listed below with a default description. You can customize these in
                                        order to provide better context to the AI model.{' '}
                                   </p>

                                   {values.reportSegment?.length > 0 && <ReportHeader name="Segment Name" description="Field Description" />}

                                   {values.reportSegment?.map((report, index) => {
                                        const valueFieldName = `reportSegment[${index}].segmentDescription`;
                                        const checkboxFieldName = `reportSegment[${index}].isSegmentCustomize`;
                                        const checkboxExcluded = `reportSegment[${index}].isExcluded`;
                                        const originalItem = parsedDescriptionOverride?.['reportSegment']?.[index];

                                        return (
                                             <CRow key={`seg-${index}`} className="mb-4">
                                                  <CCol lg="2">
                                                       <p className="f-15 mb-0 pt-2">{report.segmentName}</p>
                                                  </CCol>
                                                  <CCol lg="6">
                                                       <CTextarea
                                                            name={valueFieldName} // Use full path
                                                            value={report.segmentDescription || ''}
                                                            onChange={handleChange}
                                                            className="py-2 px-3 lh-base"
                                                            rows={3}
                                                            disabled={!report.isSegmentCustomize}
                                                       />
                                                  </CCol>
                                                  <CCol lg="1" className="d-flex align-items-center justify-content-center">
                                                       <CFormGroup variant="custom-checkbox" inline>
                                                            <CInputCheckbox
                                                                 custom
                                                                 id={checkboxExcluded}
                                                                 name={checkboxExcluded}
                                                                 checked={report.isExcluded}
                                                                 onChange={handleChange}
                                                                 className="m-auto"
                                                            />
                                                            <CLabel htmlFor={checkboxExcluded} variant="custom-checkbox">
                                                                 Exclude
                                                            </CLabel>
                                                       </CFormGroup>
                                                  </CCol>
                                                  <CCol lg="3" className="d-flex align-items-center justify-content-center">
                                                       <CFormGroup variant="custom-checkbox" inline>
                                                            <CInputCheckbox
                                                                 custom
                                                                 id={checkboxFieldName}
                                                                 name={checkboxFieldName} // Use full path
                                                                 checked={report.isSegmentCustomize}
                                                                 onChange={(e) =>
                                                                      handleCustomizeChange(
                                                                           e.target.checked,
                                                                           {
                                                                                displayName: `Segment "${report.segmentName}" Description`,
                                                                                valueFieldName: valueFieldName,
                                                                                checkboxFieldName: checkboxFieldName,
                                                                                originalDataSourceKey: 'segmentDefaultDescription', // Key in original item
                                                                                originalItem: originalItem, // Pass the specific original item
                                                                                type: 'checked',
                                                                           },
                                                                           formikSetFieldValue
                                                                      )
                                                                 }
                                                                 className="m-auto"
                                                            />
                                                            <CLabel htmlFor={checkboxFieldName} variant="custom-checkbox">
                                                                 Customize this value
                                                            </CLabel>
                                                       </CFormGroup>

                                                       {report.isSegmentCustomize && (
                                                            <CButton
                                                                 type="button"
                                                                 className="btn-sm ml-4 text-underline text-capitalize"
                                                                 color="link"
                                                                 onClick={() =>
                                                                      handleCustomizeChange(
                                                                           false,
                                                                           {
                                                                                displayName: `Segment "${report.segmentName}" Description`,
                                                                                valueFieldName: valueFieldName,
                                                                                checkboxFieldName: checkboxFieldName,
                                                                                originalDataSourceKey: 'segmentDefaultDescription', // Key in original item
                                                                                originalItem: originalItem, // Pass the specific original item
                                                                                type: 'button',
                                                                           },
                                                                           formikSetFieldValue
                                                                      )
                                                                 }
                                                            >
                                                                 Reset to default
                                                            </CButton>
                                                       )}
                                                  </CCol>
                                             </CRow>
                                        );
                                   })}
                              </div>

                              {/* Models */}
                              {values.reportModels?.length > 0 && (
                                   <div className="mt-5">
                                        <h3 className="f-15">Models</h3>
                                        <p className="f-15">
                                             If your report contains models, they are listed below with a default description. You can customize these
                                             in order to provide better context to the AI model.{' '}
                                        </p>

                                        {values.reportModels?.length > 0 && <ReportHeader name="Model Name" description="Field Description" />}

                                        {values.reportModels?.map((report, index) => {
                                             const valueFieldName = `reportModels[${index}].modelDescription`;
                                             const checkboxFieldName = `reportModels[${index}].isModelCustomize`;
                                             const checkboxExcluded = `reportModels[${index}].isExcluded`;
                                             const originalItem = parsedDescriptionOverride?.['reportModels']?.[index];

                                             return (
                                                  <CRow key={`seg-${index}`} className="mb-4">
                                                       <CCol lg="2">
                                                            <p className="f-15 mb-0 pt-2">{report.modelName}</p>
                                                       </CCol>
                                                       <CCol lg="6">
                                                            <CTextarea
                                                                 name={valueFieldName} // Use full path
                                                                 value={report.modelDescription || ''}
                                                                 onChange={handleChange}
                                                                 className="py-2 px-3 lh-base"
                                                                 rows={3}
                                                                 disabled={!report.isModelCustomize}
                                                            />
                                                       </CCol>
                                                       <CCol lg="1" className="d-flex align-items-center justify-content-center">
                                                            <CFormGroup variant="custom-checkbox" inline>
                                                                 <CInputCheckbox
                                                                      custom
                                                                      id={checkboxExcluded}
                                                                      name={checkboxExcluded}
                                                                      checked={report.isExcluded}
                                                                      onChange={handleChange}
                                                                      className="m-auto"
                                                                 />
                                                                 <CLabel htmlFor={checkboxExcluded} variant="custom-checkbox">
                                                                      Exclude
                                                                 </CLabel>
                                                            </CFormGroup>
                                                       </CCol>
                                                       <CCol lg="3" className="d-flex align-items-center justify-content-center">
                                                            <CFormGroup variant="custom-checkbox" inline>
                                                                 <CInputCheckbox
                                                                      custom
                                                                      id={checkboxFieldName}
                                                                      name={checkboxFieldName} // Use full path
                                                                      checked={report.isModelCustomize}
                                                                      onChange={(e) =>
                                                                           handleCustomizeChange(
                                                                                e.target.checked,
                                                                                {
                                                                                     displayName: `Model "${report.modelName}" Description`,
                                                                                     valueFieldName: valueFieldName,
                                                                                     checkboxFieldName: checkboxFieldName,
                                                                                     originalDataSourceKey: 'modelDefaultDescription', // Key in original item
                                                                                     originalItem: originalItem, // Pass the specific original item
                                                                                     type: 'checked',
                                                                                },
                                                                                formikSetFieldValue
                                                                           )
                                                                      }
                                                                      className="m-auto"
                                                                 />
                                                                 <CLabel htmlFor={checkboxFieldName} variant="custom-checkbox">
                                                                      Customize this value
                                                                 </CLabel>
                                                            </CFormGroup>

                                                            {report.isModelCustomize && (
                                                                 <CButton
                                                                      type="button"
                                                                      className="btn-sm ml-4 text-underline text-capitalize"
                                                                      color="link"
                                                                      onClick={() =>
                                                                           handleCustomizeChange(
                                                                                false,
                                                                                {
                                                                                     displayName: `Model "${report.segmentName}" Description`,
                                                                                     valueFieldName: valueFieldName,
                                                                                     checkboxFieldName: checkboxFieldName,
                                                                                     originalDataSourceKey: 'modelDefaultDescription', // Key in original item
                                                                                     originalItem: originalItem, // Pass the specific original item
                                                                                     type: 'button',
                                                                                },
                                                                                formikSetFieldValue
                                                                           )
                                                                      }
                                                                 >
                                                                      Reset to default
                                                                 </CButton>
                                                            )}
                                                       </CCol>
                                                  </CRow>
                                             );
                                        })}
                                   </div>
                              )}

                              {/* Dimensions & Objects */}
                              <div className="mt-5">
                                   <h3 className="f-15">Dimensions & Objects</h3>
                                   <p className="f-15">
                                        If your report has Dimensions or Objects, they are listed below with a default description. You can customize
                                        these in order to provide better context to the AI model.{' '}
                                   </p>
                                   {values.reportDimensions?.length > 0 && (
                                        <ReportHeader name="Dimension/Object Name" description="Field Description" />
                                   )}
                                   {values.reportDimensions?.map((report, index) => {
                                        const valueFieldName = `reportDimensions[${index}].dimensionDescription`;
                                        const checkboxFieldName = `reportDimensions[${index}].isDimensionCustomize`;
                                        const checkboxExcluded = `reportDimensions[${index}].isExcluded`;
                                        const originalItem = parsedDescriptionOverride?.['reportDimensions']?.[index];

                                        return (
                                             <CRow key={`dim-${index}`} className="mb-4">
                                                  <CCol lg="2">
                                                       <p className="f-15 mb-0 pt-2">{report.dimensionName}</p>
                                                  </CCol>
                                                  <CCol lg="6">
                                                       <CTextarea
                                                            name={valueFieldName} // Use full path
                                                            value={report.dimensionDescription || ''}
                                                            onChange={handleChange}
                                                            className="py-2 px-3 lh-base"
                                                            rows={3}
                                                            disabled={!report.isDimensionCustomize}
                                                       />
                                                  </CCol>
                                                  <CCol lg="1" className="d-flex align-items-center justify-content-center">
                                                       <CFormGroup variant="custom-checkbox" inline>
                                                            <CInputCheckbox
                                                                 custom
                                                                 id={checkboxExcluded}
                                                                 name={checkboxExcluded}
                                                                 checked={report.isExcluded}
                                                                 onChange={handleChange}
                                                                 className="m-auto"
                                                            />
                                                            <CLabel htmlFor={checkboxExcluded} variant="custom-checkbox">
                                                                 Exclude
                                                            </CLabel>
                                                       </CFormGroup>
                                                  </CCol>
                                                  <CCol lg="3" className="d-flex align-items-center justify-content-center">
                                                       <CFormGroup variant="custom-checkbox" inline>
                                                            <CInputCheckbox
                                                                 custom
                                                                 id={checkboxFieldName}
                                                                 name={checkboxFieldName} // Use full path
                                                                 checked={report.isDimensionCustomize}
                                                                 onChange={(e) =>
                                                                      handleCustomizeChange(
                                                                           e.target.checked,
                                                                           {
                                                                                displayName: `Dimension "${report.dimensionName}" Description`,
                                                                                valueFieldName: valueFieldName,
                                                                                checkboxFieldName: checkboxFieldName,
                                                                                originalDataSourceKey: 'dimensionDefaultDescription',
                                                                                originalItem: originalItem, // Pass the specific original item
                                                                                type: 'checked',
                                                                           },
                                                                           formikSetFieldValue
                                                                      )
                                                                 }
                                                                 className="m-auto"
                                                            />
                                                            <CLabel htmlFor={checkboxFieldName} variant="custom-checkbox">
                                                                 Customize this value
                                                            </CLabel>
                                                       </CFormGroup>

                                                       {report.isDimensionCustomize && (
                                                            <CButton
                                                                 type="button"
                                                                 className="btn-sm ml-4 text-underline text-capitalize"
                                                                 color="link"
                                                                 onClick={() =>
                                                                      handleCustomizeChange(
                                                                           false,
                                                                           {
                                                                                displayName: `Dimension "${report.dimensionName}" Description`,
                                                                                valueFieldName: valueFieldName,
                                                                                checkboxFieldName: checkboxFieldName,
                                                                                originalDataSourceKey: 'dimensionDefaultDescription',
                                                                                originalItem: originalItem,
                                                                                type: 'button',
                                                                           },
                                                                           formikSetFieldValue
                                                                      )
                                                                 }
                                                            >
                                                                 Reset to default
                                                            </CButton>
                                                       )}
                                                  </CCol>
                                             </CRow>
                                        );
                                   })}
                              </div>

                              {/* Metrics & Properties */}
                              <div className="mt-5">
                                   <h3 className="f-15">Metrics & Properties</h3>
                                   <p className="f-15">
                                        Each field from your report is listed below, along with it's default description. Customize the description to
                                        improve your AI analysis. Customizations only only apply to this template. Field Names are not editable as
                                        they are designed to match the data source for reference.{' '}
                                   </p>
                                   {values.reportMetrics?.length > 0 && <ReportHeader name="Metric/Property Name" description="Field Description" />}
                                   {values.reportMetrics?.map((report, index) => {
                                        const valueFieldName = `reportMetrics[${index}].metricDescription`;
                                        const checkboxFieldName = `reportMetrics[${index}].isMetricCustomize`;
                                        const checkboxExcluded = `reportMetrics[${index}].isExcluded`;
                                        const originalItem = parsedDescriptionOverride?.['reportMetrics']?.[index];

                                        return (
                                             <CRow key={`met-${index}`} className="mb-4">
                                                  <CCol lg="2">
                                                       <p className="f-15 mb-0 pt-2">{report.metricName}</p>
                                                  </CCol>
                                                  <CCol lg="6">
                                                       <CTextarea
                                                            name={valueFieldName} // Use full path
                                                            value={report.metricDescription || ''}
                                                            onChange={handleChange}
                                                            className="py-2 px-3 lh-base"
                                                            rows={3}
                                                            disabled={!report.isMetricCustomize}
                                                       />
                                                  </CCol>
                                                  <CCol lg="1" className="d-flex align-items-center justify-content-center">
                                                       <CFormGroup variant="custom-checkbox" inline>
                                                            <CInputCheckbox
                                                                 custom
                                                                 id={checkboxExcluded}
                                                                 name={checkboxExcluded}
                                                                 checked={report.isExcluded}
                                                                 onChange={handleChange}
                                                                 className="m-auto"
                                                            />
                                                            <CLabel htmlFor={checkboxExcluded} variant="custom-checkbox">
                                                                 Exclude
                                                            </CLabel>
                                                       </CFormGroup>
                                                  </CCol>
                                                  <CCol lg="3" className="d-flex align-items-center justify-content-center">
                                                       <CFormGroup variant="custom-checkbox" inline>
                                                            <CInputCheckbox
                                                                 custom
                                                                 id={checkboxFieldName}
                                                                 name={checkboxFieldName} // Use full path
                                                                 checked={report.isMetricCustomize}
                                                                 onChange={(e) =>
                                                                      handleCustomizeChange(
                                                                           e.target.checked,
                                                                           {
                                                                                displayName: `Metric "${report.metricName}" Description`,
                                                                                valueFieldName: valueFieldName,
                                                                                checkboxFieldName: checkboxFieldName,
                                                                                originalDataSourceKey: 'metricDefaultDescription',
                                                                                originalItem: originalItem,
                                                                                type: 'checked',
                                                                           },
                                                                           formikSetFieldValue
                                                                      )
                                                                 }
                                                                 className="m-auto"
                                                            />
                                                            <CLabel htmlFor={checkboxFieldName} variant="custom-checkbox">
                                                                 Customize this value
                                                            </CLabel>
                                                       </CFormGroup>

                                                       {report.isMetricCustomize && (
                                                            <CButton
                                                                 type="button"
                                                                 className="btn-sm ml-4 text-underline text-capitalize"
                                                                 color="link"
                                                                 onClick={() =>
                                                                      handleCustomizeChange(
                                                                           false,
                                                                           {
                                                                                displayName: `Metric "${report.metricName}" Description`,
                                                                                valueFieldName: valueFieldName,
                                                                                checkboxFieldName: checkboxFieldName,
                                                                                originalDataSourceKey: 'metricDefaultDescription',
                                                                                originalItem: originalItem,
                                                                                type: 'button',
                                                                           },
                                                                           formikSetFieldValue
                                                                      )
                                                                 }
                                                            >
                                                                 Reset to default
                                                            </CButton>
                                                       )}
                                                  </CCol>
                                             </CRow>
                                        );
                                   })}
                              </div>

                              <div className="mt-5">
                                   <CButton type="submit" className="px-4" color="primary">
                                        Save & Close
                                   </CButton>

                                   <CButton type="button" className="ml-4" color="link" onClick={handleClose}>
                                        Cancel Edits & Close
                                   </CButton>
                              </div>
                         </CForm>
                    );
                }}
            </Formik>

            {/* Reset Confirmation Modal */}
            {showResetConfirm && (
                <ConfirmSaveChange
                    show={showResetConfirm}
                    onAccept={onAcceptReset}
                    onClose={onCloseReset}
                    title="Reset to Default?"
                >
                    <p>
                        You’re about to reset the <strong>{resetData?.displayName || 'field'}</strong> to the default value from the original data source. Any customizations will be lost.
                    </p>

                    <p>Are you sure you want to proceed?</p>
                </ConfirmSaveChange>
            )}
        </div>
    );
};

export default DataSourceEditField;
